/**
 * Synthesized Web Audio API sound effects for Halloween theme.
 * Zero external audio files required - works completely client-side.
 */

let audioCtx = null;

const getAudioContext = () => {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!audioCtx) {
        audioCtx = new AudioCtx();
    }
    if (audioCtx.state === 'suspended') {
        audioCtx.resume().catch(() => {});
    }
    return audioCtx;
};

export const isSpookySoundEnabled = () => {
    if (typeof window === 'undefined') return false;
    try {
        return localStorage.getItem('psh_spooky_sound') === 'true';
    } catch {
        return false;
    }
};

export const setSpookySoundEnabled = (enabled) => {
    if (typeof window === 'undefined') return false;
    try {
        localStorage.setItem('psh_spooky_sound', enabled ? 'true' : 'false');
    } catch {
        // ignore storage errors
    }
    return enabled;
};

export const playSpookySound = (type = 'chime') => {
    if (!isSpookySoundEnabled()) return;
    try {
        const ctx = getAudioContext();
        if (!ctx) return;
        const now = ctx.currentTime;

        switch (type) {
            case 'thunder': {
                // Multi-stage realistic rolling thunder strike
                const bufferSize = ctx.sampleRate * 2.0;
                const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
                const data = buffer.getChannelData(0);
                for (let i = 0; i < bufferSize; i++) {
                    data[i] = Math.random() * 2 - 1;
                }

                const noise = ctx.createBufferSource();
                noise.buffer = buffer;

                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(320, now);
                filter.frequency.exponentialRampToValueAtTime(35, now + 1.8);

                const gain = ctx.createGain();
                gain.gain.setValueAtTime(0.01, now);
                gain.gain.linearRampToValueAtTime(0.5, now + 0.05);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 1.9);

                noise.connect(filter);
                filter.connect(gain);
                gain.connect(ctx.destination);

                noise.start(now);
                noise.stop(now + 2.0);
                break;
            }

            case 'howl': {
                // Spectral wolf / eerie wind howl
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(220, now);
                osc.frequency.exponentialRampToValueAtTime(440, now + 0.8);
                osc.frequency.exponentialRampToValueAtTime(180, now + 2.2);

                gain.gain.setValueAtTime(0.001, now);
                gain.gain.linearRampToValueAtTime(0.18, now + 0.4);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + 2.3);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now);
                osc.stop(now + 2.4);
                break;
            }

            case 'chime': {
                // Eerie minor-third harmonic glass chime
                const freqs = [311.13, 369.99, 466.16]; // Eb4, Gb4, Bb4 (minor)
                freqs.forEach((freq, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, now + idx * 0.08);

                    gain.gain.setValueAtTime(0.001, now + idx * 0.08);
                    gain.gain.linearRampToValueAtTime(0.12 / (idx + 1), now + idx * 0.08 + 0.04);
                    gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.08 + 1.2);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now + idx * 0.08);
                    osc.stop(now + idx * 0.08 + 1.3);
                });
                break;
            }

            case 'cackle': {
                // Eerie descending wobble witch cackle
                const notes = [680, 620, 560, 520, 480, 440, 390, 340, 300];
                notes.forEach((freq, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sawtooth';
                    osc.frequency.setValueAtTime(freq, now + idx * 0.07);

                    gain.gain.setValueAtTime(0.09, now + idx * 0.07);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + idx * 0.07 + 0.09);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now + idx * 0.07);
                    osc.stop(now + idx * 0.07 + 0.1);
                });
                break;
            }

            case 'pop': {
                // Ghost banish pop
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(520, now);
                osc.frequency.exponentialRampToValueAtTime(90, now + 0.16);

                gain.gain.setValueAtTime(0.25, now);
                gain.gain.exponentialRampToValueAtTime(0.001, now + 0.16);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(now);
                osc.stop(now + 0.17);
                break;
            }

            case 'flutter': {
                // Bat wings flutter
                for (let i = 0; i < 5; i++) {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(240 + (i % 2) * 60, now + i * 0.05);

                    gain.gain.setValueAtTime(0.1, now + i * 0.05);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.05 + 0.05);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(now + i * 0.05);
                    osc.stop(now + i * 0.05 + 0.06);
                }
                break;
            }

            default:
                break;
        }
    } catch {
        // audio playback failure silently ignored
    }
};
